import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertTriangle, TrendingUp, TrendingDown, Package, ClipboardList, ShoppingCart,
  Truck, Activity, Clock, Users, Wallet, FileText, ArrowRight, Banknote,
  PieChart, Flame, Building2, ArrowDownCircle, ArrowUpCircle, BarChart3, CloudOff, PackageCheck, ClipboardCheck, ShieldAlert, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate as fmtDateTz, fmtDateTime as fmtDateTimeTz } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n, currency = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency }).format(Number(n || 0));
const fmtNum = (n) => new Intl.NumberFormat('es-AR').format(Number(n || 0));
const fmtDate = (s) => (s ? fmtDateTz(s, { year: '2-digit' }) : '-');
const fmtDateTime = (s) => (s ? fmtDateTimeTz(s, { year: undefined }) : '-');

const CHECK_STATUS_LABELS = {
  pending: 'Pendiente',
  deposited: 'Depositado',
  cashed: 'Cobrado',
  returned: 'Rechazado',
  cancelled: 'Anulado',
  unknown: 'Sin estado',
};

function Dashboard() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/stats/dashboard-v2`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error('Error al cargar el tablero');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="dashboard-loading">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const perms = data.permissions || {};
  const isAdmin = data.user_role === 'admin';
  const canSee = (k) => isAdmin || perms[k] === true;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2">Tablero</h1>
        <p className="text-muted-foreground">Bienvenido, {user?.name}</p>
      </div>

      {/* BLOQUE 1 — Alertas Críticas */}
      {canSee('alertas_criticas')
        ? <AlertsBlock data={data.alerts} navigate={navigate} />
        : ((data.alerts?.pending_homologation_total || 0) > 0 &&
            <AlertsBlock data={data.alerts} navigate={navigate} restricted />)}

      {/* BLOQUE 2 — Inventario */}
      <InventoryBlock data={data.inventory} canSee={canSee} navigate={navigate} />

      {/* BLOQUE 3 — Flujo de Compras */}
      <PurchaseFlowBlock data={data.purchase_flow} canSee={canSee} navigate={navigate} />

      {/* BLOQUE 4 — Pedidos Internos */}
      <InternalRequestsBlock data={data.internal_requests} canSee={canSee} navigate={navigate} awaitingCount={data.alerts?.opis_awaiting_receipt_total || 0} showAwaiting={!canSee('alertas_criticas')} />

      {/* BLOQUE 5 — Tesorería */}
      <TreasuryBlock data={data.treasury} canSee={canSee} navigate={navigate} />

      {/* BLOQUE 6 — Actividad & Equipo (admin) */}
      {isAdmin && <TeamBlock data={data.team_activity} canSee={canSee} navigate={navigate} />}
    </div>
  );
}

// Banner de Homologaciones pendientes — alerta propia, gobernada por el permiso
// `articulos.estabilizar` (el backend ya devuelve 0 si el usuario no lo tiene).
// Se muestra INDEPENDIENTE del widget `dashboard.alertas_criticas`.
function HomologationBanner({ total, navigate }) {
  if ((total || 0) <= 0) return null;
  return (
    <button
      onClick={() => navigate('/articles?tab=stabilize')}
      data-testid="alert-pending-homologation"
      className="w-full mb-3 rounded-lg border-2 border-red-500 bg-red-100 text-red-900 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-100 p-3 flex items-center gap-3 text-left transition-colors cursor-pointer animate-pulse-slow"
    >
      <ShieldCheck className="w-6 h-6 shrink-0" />
      <div className="flex-1">
        <div className="text-xs font-semibold uppercase flex items-center gap-2">
          Homologaciones pendientes
          <Badge className="bg-red-600 text-white">{fmtNum(total)}</Badge>
        </div>
        <div className="text-sm">
          Fichas de alta por REM esperando que Farmacia las valide. Mientras no se resuelvan, no aparecen en Compras (NP/OC) y el stock queda arrinconado. Tocá para abrir Homologación &amp; Fusión.
        </div>
      </div>
      <ArrowRight className="w-5 h-5 shrink-0" />
    </button>
  );
}

// =========================================================
// BLOQUE 1 — ALERTAS CRÍTICAS
// =========================================================
function AlertsBlock({ data, navigate, restricted }) {
  if (!data) return null;

  // Modo restringido: el usuario NO tiene el widget `alertas_criticas` pero SÍ el
  // permiso de homologación (backend devolvió total > 0). Mostramos sólo ese banner.
  if (restricted) {
    if ((data.pending_homologation_total || 0) <= 0) return null;
    return (
      <Card className="border-red-300 bg-red-50/40 dark:bg-red-950/20" data-testid="block-alerts">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <Flame className="w-5 h-5" /> Alertas Críticas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <HomologationBanner total={data.pending_homologation_total} navigate={navigate} />
        </CardContent>
      </Card>
    );
  }

  const hasAny =
    (data.np_urgent_overdue_total || 0) > 0 ||
    (data.receptions_conditional_total || 0) > 0 ||
    (data.expired_lots_count || 0) > 0 ||
    (data.checks_critical_total || 0) > 0 ||
    (data.opis_awaiting_receipt_total || 0) > 0 ||
    (data.stock_counts_unadjusted_total || 0) > 0 ||
    (data.pending_homologation_total || 0) > 0 ||
    data.arca_cert_days_left !== null && data.arca_cert_days_left !== undefined ||
    data.drive_backup_failed;

  if (!hasAny) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20" data-testid="block-alerts-clean">
        <CardContent className="py-4 flex items-center gap-3 text-emerald-700 dark:text-emerald-300">
          <Activity className="w-5 h-5" />
          <span className="font-medium">Sin alertas críticas. Todo bajo control 👌</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-300 bg-red-50/40 dark:bg-red-950/20" data-testid="block-alerts">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
          <Flame className="w-5 h-5" /> Alertas Críticas
        </CardTitle>
      </CardHeader>
      <CardContent>
        {(data.arca_cert_days_left !== null && data.arca_cert_days_left !== undefined) && (
          <button
            onClick={() => navigate('/arca-config')}
            data-testid="alert-arca-cert-expiring"
            className="w-full mb-3 rounded-lg border border-amber-400 bg-amber-100/70 text-amber-900 hover:bg-amber-200/70 dark:bg-amber-900/30 dark:text-amber-200 p-3 flex items-center gap-3 text-left transition-colors cursor-pointer"
          >
            <ShieldAlert className="w-6 h-6 shrink-0" />
            <div>
              <div className="text-xs font-semibold uppercase">
                {data.arca_cert_expired ? 'Certificado de ARCA vencido' : 'Certificado de ARCA por vencer'}
              </div>
              <div className="text-sm">
                {data.arca_cert_expired
                  ? 'No se pueden emitir comprobantes fiscales hasta renovarlo. Tocá para ir a Facturación ARCA.'
                  : `Vence en ${data.arca_cert_days_left} días. Renovalo antes para no cortar la facturación electrónica.`}
              </div>
            </div>
          </button>
        )}
        {data.drive_backup_failed && (
          <button
            onClick={() => navigate('/maintenance-center')}
            data-testid="alert-drive-backup-failed"
            className="w-full mb-3 rounded-lg border border-red-400 bg-red-100/70 text-red-800 hover:bg-red-200/70 dark:bg-red-900/30 dark:text-red-200 p-3 flex items-center gap-3 text-left transition-colors cursor-pointer"
          >
            <CloudOff className="w-6 h-6 shrink-0" />
            <div>
              <div className="text-xs font-semibold uppercase">Backup a Google Drive falló</div>
              <div className="text-sm">El último backup automático no se pudo subir a la nube. Tocá para revisar en Centro de Mantenimiento → Tareas programadas.</div>
            </div>
          </button>
        )}
        {(data.pending_homologation_total || 0) > 0 && (
          <HomologationBanner total={data.pending_homologation_total} navigate={navigate} />
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <AlertTile
            count={data.np_urgent_overdue_total}
            label="NP urgentes/vencidas"
            icon={<ClipboardList className="w-4 h-4" />}
            color="red"
            onClick={() => navigate('/purchase-requests?status=pending')}
            testId="alert-np-urgent"
          />
          <AlertTile
            count={data.receptions_conditional_total}
            label="Recepciones condicionales"
            icon={<Truck className="w-4 h-4" />}
            color="amber"
            onClick={() => navigate('/receptions?status=conditional')}
            testId="alert-receptions-cond"
          />
          <AlertTile
            count={data.expired_lots_count}
            label="Lotes vencidos en stock"
            icon={<AlertTriangle className="w-4 h-4" />}
            color="red"
            onClick={() => navigate('/reports?tab=expirations&expired=1')}
            testId="alert-expired-lots"
          />
          <AlertTile
            count={data.checks_critical_total}
            label="Cheques críticos / rechazados"
            icon={<Banknote className="w-4 h-4" />}
            color="rose"
            onClick={() => navigate('/treasury')}
            testId="alert-checks"
          />
          <AlertTile
            count={data.opis_awaiting_receipt_total}
            label="OPIs despachadas sin recibir"
            icon={<PackageCheck className="w-4 h-4" />}
            color="teal"
            onClick={() => navigate('/internal-requests?pending_receipt=1')}
            testId="alert-opis-awaiting-receipt"
          />
          <AlertTile
            count={data.stock_counts_unadjusted_total}
            label="Conteos con diferencias sin ajustar"
            icon={<ClipboardCheck className="w-4 h-4" />}
            color="amber"
            onClick={() => navigate('/stock-counts')}
            testId="alert-counts-unadjusted"
          />
        </div>
      </CardContent>
    </Card>
  );
}

const AlertTile = ({ count, label, icon, color, onClick, testId }) => {
  if (!count || count === 0) return null;
  const colors = {
    red: 'border-red-300 bg-red-100/60 text-red-800 hover:bg-red-200/60 dark:bg-red-900/30 dark:text-red-200',
    amber: 'border-amber-300 bg-amber-100/60 text-amber-800 hover:bg-amber-200/60 dark:bg-amber-900/30 dark:text-amber-200',
    rose: 'border-rose-300 bg-rose-100/60 text-rose-800 hover:bg-rose-200/60 dark:bg-rose-900/30 dark:text-rose-200',
    teal: 'border-teal-300 bg-teal-100/60 text-teal-800 hover:bg-teal-200/60 dark:bg-teal-900/30 dark:text-teal-200',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`rounded-lg border p-3 text-left transition-colors cursor-pointer ${colors[color]}`}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase">{icon}{label}</div>
      <div className="text-3xl font-bold mt-1">{count}</div>
      <div className="text-xs flex items-center gap-1 mt-1 opacity-80">Revisar <ArrowRight className="w-3 h-3" /></div>
    </button>
  );
};

// =========================================================
// BLOQUE 2 — INVENTARIO
// =========================================================
function InventoryBlock({ data, canSee, navigate }) {
  if (!data) return null;
  const showAny = ['inv_valor_total', 'inv_vencimientos_heatmap', 'inv_top_criticos', 'inv_pareto'].some(canSee);
  if (!showAny) return null;

  return (
    <section data-testid="block-inventory">
      <h2 className="text-2xl font-semibold flex items-center gap-2 mb-4">
        <Package className="w-6 h-6 text-blue-600" /> Inventario y Vencimientos
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {canSee('inv_valor_total') && (
          <Card data-testid="widget-inv-valor-total" className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase">Valor Total Stock</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{fmtMoney(data.total_value)}</p>
              <p className={`text-sm mt-1 flex items-center gap-1 ${data.variation_pct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {data.variation_pct >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {data.variation_pct >= 0 ? '+' : ''}{data.variation_pct}% vs mes anterior
              </p>
              <p className="text-xs text-muted-foreground mt-1">Mes anterior: {fmtMoney(data.prev_month_value)}</p>
            </CardContent>
          </Card>
        )}

        {canSee('inv_top_criticos') && (
          <Card data-testid="widget-inv-top-criticos" className="lg:col-span-2 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/reports')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Top Críticos (Stock ≤ Reposición)</span>
                {data.critical_total > 0 && <Badge variant="destructive">{data.critical_total}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_critical?.length > 0 ? (
                <div className="space-y-1">
                  {data.top_critical.slice(0, 5).map((a) => (
                    <div key={a.article_id} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{a.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">· {a.warehouse_name}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono text-xs">{a.stock}/{a.reorder_point}</span>
                        <Badge variant="outline" className="text-xs">{a.urgency_pct}% urg</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin artículos en estado crítico</p>}
            </CardContent>
          </Card>
        )}

        {canSee('inv_vencimientos_heatmap') && (
          <Card data-testid="widget-inv-vencimientos" className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Mapa de Calor — Vencimientos por Depósito</CardTitle>
            </CardHeader>
            <CardContent>
              {data.expirations_heatmap?.length > 0 ? (
                <div className="space-y-1 text-sm">
                  <div className="grid grid-cols-5 text-xs font-semibold text-muted-foreground pb-1 border-b uppercase">
                    <span>Depósito</span><span className="text-center text-red-700">Vencidos</span><span className="text-center text-orange-700">≤30d</span><span className="text-center text-amber-700">≤60d</span><span className="text-center text-emerald-700">≤90d</span>
                  </div>
                  {data.expirations_heatmap.map((h, i) => (
                    <div key={i} className="grid grid-cols-5 items-center text-sm py-1">
                      <span className="truncate font-medium">{h.warehouse_name}</span>
                      <HeatCell value={h.vencido} qty={h.vencido_qty} tone="red" />
                      <HeatCell value={h.d30} qty={h.d30_qty} tone="orange" />
                      <HeatCell value={h.d60} qty={h.d60_qty} tone="amber" />
                      <HeatCell value={h.d90} qty={h.d90_qty} tone="emerald" />
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin vencimientos a 90 días</p>}
            </CardContent>
          </Card>
        )}

        {canSee('inv_pareto') && (
          <Card data-testid="widget-inv-pareto">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><PieChart className="w-4 h-4 text-violet-600" />Pareto 80%</CardTitle>
            </CardHeader>
            <CardContent>
              {data.pareto_80?.length > 0 ? (
                <>
                  <p className="text-xs text-muted-foreground mb-2">
                    <strong>{data.pareto_articles_count}</strong> artículos = 80% del valor
                    <span className="text-muted-foreground"> · de {data.pareto_total_articles} totales</span>
                  </p>
                  <div className="space-y-1 max-h-48 overflow-auto">
                    {data.pareto_80.slice(0, 8).map((a, i) => (
                      <div key={i} className="flex justify-between items-center text-xs py-1">
                        <span className="truncate flex-1 mr-2">{a.name}</span>
                        <div className="flex gap-2 items-center shrink-0">
                          <span className="font-mono text-muted-foreground">{a.cum_pct}%</span>
                          <Badge variant="outline" className="text-xs">{fmtMoney(a.value)}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">Sin datos de Pareto</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}

const HeatCell = ({ value, qty, tone }) => {
  const tones = {
    red: value > 0 ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' : 'text-muted-foreground',
    orange: value > 0 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' : 'text-muted-foreground',
    amber: value > 0 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'text-muted-foreground',
    emerald: value > 0 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'text-muted-foreground',
  };
  return (
    <span className={`text-center rounded px-1 py-0.5 text-xs font-mono ${tones[tone]}`}>
      {value || 0}{value > 0 && <span className="text-[10px] opacity-70"> ({fmtNum(qty)}u)</span>}
    </span>
  );
};

// =========================================================
// BLOQUE 3 — FLUJO DE COMPRAS
// =========================================================
function PurchaseFlowBlock({ data, canSee, navigate }) {
  if (!data) return null;
  const showAny = ['flujo_embudo', 'flujo_lead_time', 'flujo_demanda_insatisfecha', 'flujo_top_proveedores'].some(canSee);
  if (!showAny) return null;

  return (
    <section data-testid="block-purchase-flow">
      <h2 className="text-2xl font-semibold flex items-center gap-2 mb-4">
        <ShoppingCart className="w-6 h-6 text-violet-600" /> Flujo de Compras
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {canSee('flujo_embudo') && (
          <Card data-testid="widget-flujo-embudo" className="lg:col-span-2">
            <CardHeader className="pb-2"><CardTitle className="text-base">Embudo NP → OC → Recepción</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                <FunnelStep label="NP Pendientes" value={data.funnel.np_pending} color="amber" onClick={() => navigate('/purchase-requests?status=pending')} />
                <FunnelStep label="NP Aprobadas" value={data.funnel.np_approved_open} color="blue" onClick={() => navigate('/purchase-requests')} />
                <FunnelStep label="OC Generadas" value={data.funnel.oc_generated} color="violet" onClick={() => navigate('/purchase-orders?status=generated')} />
                <FunnelStep label="OC Recibidas" value={data.funnel.oc_received_30d} color="emerald" onClick={() => navigate('/purchase-orders')} />
              </div>
            </CardContent>
          </Card>
        )}

        {canSee('flujo_lead_time') && (
          <Card data-testid="widget-lead-time">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" />Tiempo de Gestión (últimos 60 días)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">NP → OC</p>
                <p className="text-2xl font-bold">{data.lead_time.np_to_oc_days}d</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">OC → Recepción</p>
                <p className="text-2xl font-bold">{data.lead_time.oc_to_rec_days}d</p>
              </div>
              <p className="text-xs text-muted-foreground border-t pt-1">Muestra: {data.lead_time.sample_size} operaciones</p>
            </CardContent>
          </Card>
        )}

        {canSee('flujo_demanda_insatisfecha') && (
          <Card data-testid="widget-demanda" className="lg:col-span-2 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/purchases/unmet-demand')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-600" />Demanda Insatisfecha</span>
                {data.unmet_demand.items_count > 0 && <Badge variant="destructive">{data.unmet_demand.items_count} arts</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.unmet_demand.top?.length > 0 ? (
                <div className="space-y-1 text-sm">
                  {data.unmet_demand.top.slice(0, 5).map((u) => (
                    <div key={u.article_id} className="flex justify-between items-center py-1 border-b last:border-0">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{u.article_name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{u.areas.join(', ')}</span>
                      </div>
                      <Badge variant="outline" className="text-rose-700 border-rose-300">faltan {u.shortage}</Badge>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No hay demanda insatisfecha</p>}
            </CardContent>
          </Card>
        )}

        {canSee('flujo_top_proveedores') && (
          <Card data-testid="widget-top-proveedores">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Truck className="w-4 h-4 text-violet-600" />Top Proveedores del Mes</CardTitle></CardHeader>
            <CardContent>
              {data.top_suppliers?.length > 0 ? (
                <div className="space-y-1">
                  {data.top_suppliers.map((s, i) => (
                    <div key={i} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                      <span className="truncate flex-1">{s.supplier}</span>
                      <div className="flex gap-2 items-center shrink-0">
                        <span className="text-xs text-muted-foreground">{s.orders} OC</span>
                        <span className="font-mono text-xs">{fmtMoney(s.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin OC en el mes</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}

const FunnelStep = ({ label, value, color, onClick }) => {
  const colors = {
    amber: 'border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200',
    blue: 'border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200',
    violet: 'border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800 dark:bg-violet-950/30 dark:text-violet-200',
    emerald: 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200',
  };
  return (
    <button onClick={onClick} className={`rounded-lg border p-3 text-center transition-colors cursor-pointer ${colors[color]}`}>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-xs uppercase mt-1">{label}</div>
    </button>
  );
};

// =========================================================
// BLOQUE 4 — PEDIDOS INTERNOS
// =========================================================
function InternalRequestsBlock({ data, canSee, navigate, awaitingCount = 0, showAwaiting = false }) {
  if (!data) return null;
  const showAny = ['opi_pendientes', 'opi_faltante_pct', 'opi_top_areas', 'opi_rem_mes'].some(canSee);
  const showAwaitingCard = showAwaiting && awaitingCount > 0;
  if (!showAny && !showAwaitingCard) return null;

  return (
    <section data-testid="block-opi">
      <h2 className="text-2xl font-semibold flex items-center gap-2 mb-4">
        <ClipboardList className="w-6 h-6 text-cyan-600" /> Pedidos Internos y Enfermería
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {showAwaitingCard && (
          <Card
            data-testid="opi-awaiting-card"
            className="lg:col-span-3 border-teal-300 bg-teal-50/70 hover:bg-teal-100/70 transition-colors cursor-pointer"
            onClick={() => navigate('/internal-requests?pending_receipt=1')}
          >
            <CardContent className="py-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-teal-600/10 p-2 text-teal-700 shrink-0"><PackageCheck className="w-5 h-5" /></div>
                <div>
                  <div className="font-semibold text-teal-800 flex items-center gap-2">
                    OPIs despachadas sin recibir
                    <Badge className="bg-teal-600">{awaitingCount}</Badge>
                  </div>
                  <p className="text-xs text-teal-700/80">Mercadería despachada esperando que confirmes la recepción para que ingrese al stock.</p>
                </div>
              </div>
              <span className="text-sm font-medium text-teal-700 flex items-center gap-1 shrink-0">Ver <ArrowRight className="w-4 h-4" /></span>
            </CardContent>
          </Card>
        )}
        {canSee('opi_pendientes') && (
          <Card data-testid="widget-opi-pendientes" className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/internal-requests')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>OPI Pendientes</span>
                {data.pending_total > 0 && <Badge className="bg-cyan-600">{data.pending_total}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.pending_list?.length > 0 ? (
                <div className="space-y-1 text-xs">
                  {data.pending_list.slice(0, 5).map((p) => (
                    <div key={p.request_id} className="flex justify-between items-center py-1 border-b last:border-0">
                      <div className="truncate">
                        <span className="font-mono">{p.request_number}</span>
                        <span className="text-muted-foreground ml-1">· {p.area_name}</span>
                      </div>
                      <Badge variant="outline" className="text-[10px] capitalize">{p.priority}</Badge>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin OPI pendientes</p>}
            </CardContent>
          </Card>
        )}

        {canSee('opi_faltante_pct') && (
          <Card data-testid="widget-opi-faltante">
            <CardHeader className="pb-2"><CardTitle className="text-base">% con Faltante (30d)</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <p className={`text-4xl font-bold ${data.shortage_pct_30d > 30 ? 'text-rose-600' : data.shortage_pct_30d > 10 ? 'text-amber-600' : 'text-emerald-600'}`}>{data.shortage_pct_30d}%</p>
                <p className="text-sm text-muted-foreground">de {data.total_count_30d}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{data.shortage_count_30d} pedidos con shortage</p>
              <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full ${data.shortage_pct_30d > 30 ? 'bg-rose-500' : data.shortage_pct_30d > 10 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, data.shortage_pct_30d)}%` }} />
              </div>
            </CardContent>
          </Card>
        )}

        {canSee('opi_top_areas') && (
          <Card data-testid="widget-opi-top-areas">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" />Top Áreas del Mes</CardTitle></CardHeader>
            <CardContent>
              {data.top_areas_month?.length > 0 ? (
                <div className="space-y-1 text-sm">
                  {data.top_areas_month.map((a, i) => (
                    <div key={i} className="flex justify-between items-center py-1 border-b last:border-0">
                      <span className="truncate flex-1">{a.area_name}</span>
                      <div className="flex gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground">{a.opis} OPI</span>
                        <Badge variant="outline" className="text-xs">{fmtNum(a.qty)}u</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin consumos en el mes</p>}
            </CardContent>
          </Card>
        )}

        {canSee('opi_rem_mes') && (
          <Card data-testid="widget-opi-rem" className="lg:col-span-3 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/nursing-receptions')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>REM del Mes (Familiares)</span>
                <div className="flex gap-2">
                  <Badge variant="outline">{data.rem_month.count} recepciones</Badge>
                  <Badge variant="outline">{fmtNum(data.rem_month.items_total)} unid.</Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.rem_month.top_patients?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {data.rem_month.top_patients.map((p, i) => (
                    <div key={i} className="rounded-md border p-2 text-sm">
                      <div className="font-medium truncate">{p.patient}</div>
                      <div className="text-xs text-muted-foreground">{fmtNum(p.qty)} unidades</div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin REM en el mes</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}

// =========================================================
// BLOQUE 5 — TESORERÍA
// =========================================================
function TreasuryBlock({ data, canSee, navigate }) {
  if (!data) return null;
  const showAny = ['tes_saldo_consolidado', 'tes_cheques_propios', 'tes_cheques_terceros', 'tes_top_conceptos', 'tes_op_pendientes'].some(canSee);
  if (!showAny) return null;

  return (
    <section data-testid="block-treasury">
      <h2 className="text-2xl font-semibold flex items-center gap-2 mb-4">
        <Wallet className="w-6 h-6 text-emerald-600" /> Tesorería
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {canSee('tes_saldo_consolidado') && (
          <Card data-testid="widget-tes-saldo" className="lg:col-span-2 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/treasury')}>
            <CardHeader className="pb-2"><CardTitle className="text-base">Saldo Consolidado por Moneda</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {data.consolidated_balances?.length > 0 ? data.consolidated_balances.map((b, i) => (
                  <div key={i} className="rounded-lg border bg-emerald-50/50 dark:bg-emerald-950/20 p-3">
                    <div className="text-xs text-muted-foreground uppercase">{b.currency}</div>
                    <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{fmtMoney(b.total, b.currency)}</div>
                    <div className="text-xs text-muted-foreground mt-1">{b.accounts} {b.accounts === 1 ? 'cuenta' : 'cuentas'}</div>
                  </div>
                )) : <p className="col-span-3 text-sm text-muted-foreground">Sin cajas/bancos activos</p>}
              </div>
              {data.accounts_detail?.length > 0 && (
                <div className="mt-3 pt-3 border-t space-y-1">
                  {data.accounts_detail.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex justify-between items-center text-xs py-1">
                      <span className="truncate">{a.name} <span className="text-muted-foreground">({a.entity_type})</span></span>
                      <span className={`font-mono ${a.balance < 0 ? 'text-rose-600' : ''}`}>{fmtMoney(a.balance, a.currency)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {canSee('tes_cheques_propios') && (
          <Card data-testid="widget-tes-cheq-propios">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Banknote className="w-4 h-4 text-amber-600" />Cheques por Vencer</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <BucketRow label="Próximos 7 días" value={data.checks_propios_upcoming.d7} color="red" />
              <BucketRow label="Entre 8 y 15 días" value={data.checks_propios_upcoming.d15} color="amber" />
              <BucketRow label="Entre 16 y 30 días" value={data.checks_propios_upcoming.d30} color="emerald" />
            </CardContent>
          </Card>
        )}

        {canSee('tes_cheques_terceros') && (
          <Card data-testid="widget-tes-cheq-terceros">
            <CardHeader className="pb-2"><CardTitle className="text-base">Cheques en Cartera</CardTitle></CardHeader>
            <CardContent>
              {data.checks_by_status?.length > 0 ? (
                <div className="space-y-1">
                  {data.checks_by_status.map((s, i) => (
                    <div key={i} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                      <Badge variant="outline" className="text-xs">{CHECK_STATUS_LABELS[s.status] || s.status}</Badge>
                      <div className="flex gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground">{s.count}</span>
                        <span className="font-mono text-xs">{fmtMoney(s.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin cheques en cartera</p>}
            </CardContent>
          </Card>
        )}

        {canSee('tes_top_conceptos') && (
          <Card data-testid="widget-tes-conceptos" className="lg:col-span-2">
            <CardHeader className="pb-2"><CardTitle className="text-base">Top Conceptos del Mes</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1"><ArrowUpCircle className="w-3 h-3" />Ingresos</p>
                  {data.top_incomes_month?.length > 0 ? data.top_incomes_month.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm py-0.5">
                      <span className="truncate flex-1 mr-2">{c.concept}</span>
                      <span className="font-mono text-emerald-600 text-xs">{fmtMoney(c.amount)}</span>
                    </div>
                  )) : <p className="text-xs text-muted-foreground">Sin ingresos</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold text-rose-700 mb-1 flex items-center gap-1"><ArrowDownCircle className="w-3 h-3" />Egresos</p>
                  {data.top_expenses_month?.length > 0 ? data.top_expenses_month.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm py-0.5">
                      <span className="truncate flex-1 mr-2">{c.concept}</span>
                      <span className="font-mono text-rose-600 text-xs">{fmtMoney(c.amount)}</span>
                    </div>
                  )) : <p className="text-xs text-muted-foreground">Sin egresos</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {canSee('tes_op_pendientes') && (
          <Card data-testid="widget-tes-op">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" />Órd. de Pago del Mes</CardTitle></CardHeader>
            <CardContent>
              <p className="text-4xl font-bold">{data.payment_orders_month}</p>
              <p className="text-xs text-muted-foreground mt-1">emitidas este mes</p>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}

const BucketRow = ({ label, value, color }) => {
  const colors = {
    red: 'text-rose-600',
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
  };
  return (
    <div className="flex justify-between items-center py-1 border-b last:border-0">
      <span className="text-sm">{label}</span>
      <span className={`text-2xl font-bold ${colors[color]}`}>{value}</span>
    </div>
  );
};

// =========================================================
// BLOQUE 6 — ACTIVIDAD & EQUIPO (ADMIN)
// =========================================================
function TeamBlock({ data, canSee, navigate }) {
  if (!data || Object.keys(data).length === 0) return null;
  const showAny = ['act_top_usuarios', 'act_audit_critico', 'act_compras_12m'].some(canSee);
  if (!showAny) return null;

  return (
    <section data-testid="block-team">
      <h2 className="text-2xl font-semibold flex items-center gap-2 mb-4">
        <Users className="w-6 h-6 text-slate-600" /> Actividad y Equipo
        <Badge variant="outline" className="text-xs">Admin</Badge>
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {canSee('act_top_usuarios') && (
          <Card data-testid="widget-team-top-users">
            <CardHeader className="pb-2"><CardTitle className="text-base">Usuarios Activos (7d)</CardTitle></CardHeader>
            <CardContent>
              {data.top_users?.length > 0 ? (
                <div className="space-y-1 text-sm">
                  {data.top_users.map((u) => (
                    <div key={u.user_id} className="flex justify-between items-center py-1 border-b last:border-0">
                      <span className="truncate flex-1">{u.name}</span>
                      <div className="flex gap-2 shrink-0">
                        <Badge variant="outline" className="text-xs">{u.actions_today} hoy</Badge>
                        <Badge variant="secondary" className="text-xs">{u.actions_week} sem</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin actividad reciente</p>}
            </CardContent>
          </Card>
        )}

        {canSee('act_audit_critico') && (
          <Card data-testid="widget-team-audit" className="lg:col-span-2 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/audit')}>
            <CardHeader className="pb-2"><CardTitle className="text-base">Acciones Críticas Recientes</CardTitle></CardHeader>
            <CardContent>
              {data.critical_audit?.length > 0 ? (
                <div className="space-y-1 max-h-56 overflow-auto">
                  {data.critical_audit.map((l) => (
                    <div key={l.log_id} className="flex items-start justify-between text-xs py-1 border-b last:border-0">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{l.user_name}</span>
                        <span className="text-muted-foreground"> · {l.module}</span>
                        <p className="text-muted-foreground truncate">{l.description}</p>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <Badge variant="outline" className="text-[10px]">{l.action}</Badge>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(l.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Sin acciones críticas</p>}
            </CardContent>
          </Card>
        )}

        {canSee('act_compras_12m') && (
          <Card data-testid="widget-team-purchases-12m" className="lg:col-span-3">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" />Compras Últimos 12 Meses</CardTitle></CardHeader>
            <CardContent>
              {data.purchases_12m?.length > 0 ? (
                <Chart12Months data={data.purchases_12m} />
              ) : <p className="text-sm text-muted-foreground">Sin OC en los últimos 12 meses</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}

const Chart12Months = ({ data }) => {
  const maxAmount = Math.max(...data.map(d => d.amount), 1);
  return (
    <div className="flex items-end justify-between gap-1 h-40 px-2 border-b">
      {data.map((d, i) => {
        const h = (d.amount / maxAmount) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center group relative">
            <div
              className="w-full bg-violet-500 hover:bg-violet-600 rounded-t transition-colors cursor-pointer"
              style={{ height: `${Math.max(h, 4)}%` }}
              title={`${d.month}: ${fmtMoney(d.amount)} (${d.orders} OC)`}
            />
            <span className="text-[10px] text-muted-foreground mt-1 rotate-45 origin-left">{d.month}</span>
          </div>
        );
      })}
    </div>
  );
};

export default Dashboard;
